import { useState, useEffect, useContext } from 'react';
import { Avatar, Button, Input, message } from 'antd';
import AppPopover from '../../../../../components/popover/popover';
import { UserOutlined, MailFilled, PhoneFilled } from '@ant-design/icons';
import { SLOT_STATUS } from './time-selector';
import { Appointments } from '../../../../../models';
import { GlobalContext } from 'soxo-bootstrap-core';
import { formatToDDMMYYYY } from '../../../../../utils/date.utils';
import { getTimeRemaining } from '../rendering-components';
import './block-slot-popover.scss';

const { TextArea } = Input;

const useResponsivePopover = () => {
    const getPopoverProps = (width) => {
        if (width < 768) {
            return {
                placement: 'top',
                trigger: 'click',
            };
        }
        return {
            placement: 'rightTop',
            trigger: ['hover', 'click'],
        };
    };

    const [popoverProps, setPopoverProps] = useState(getPopoverProps(window.innerWidth));

    useEffect(() => {
        const handleResize = () => {
            setPopoverProps(getPopoverProps(window.innerWidth));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return popoverProps;
};

/**  Blocked Slot Popover */
export function BlockedSlotPopover({ entry, children, setShouldFetch }) {
    const { settings } = useContext(GlobalContext);
    const popoverProps = useResponsivePopover();

    const [showReasonBox, setShowReasonBox] = useState(false);

    const [reason, setReason] = useState('');

    const [loading, setLoading] = useState(false);

    const [timeRemaining, setTimeRemaining] = useState('');

    const slotSettings = settings?.SLOTSET;
    const releasePermission = slotSettings?.userRelease;

    // parse other_details JSON
    let otherDetails = {};
    try {
        otherDetails = entry?.other_details ? JSON.parse(entry.other_details) : {};
    } catch (err) {
        console.error('Invalid other_details JSON', err);
    }

    const { remarks, reason: blockReason, package_code, user } = otherDetails;

    useEffect(() => {
        // Update remaining time every minute
        const updateTime = () => setTimeRemaining(getTimeRemaining(entry.expiry_time));
        // Run immediately on mount so UI shows the correct initial countdown
        updateTime();
        // Then schedule updates every 60 seconds
        const interval = setInterval(updateTime, 60000);

        // Cleanup: stop the interval when component unmounts or expiry changes
        return () => clearInterval(interval);
    }, [entry.expiry_time]);

    // Fallback
    if (entry.sub_mode !== SLOT_STATUS.BLOCKED) return children;

    // Handle Relase Slot
    const handleRelease = async () => {
        if (!showReasonBox) {
            setShowReasonBox(true);
            return;
        }
        if (!reason.trim()) {
            message.warning('Please provide a reason before releasing.');
            return;
        }

        const payload = [
            {
                token_no: entry.token_no,
                app_time: entry.as_starttime,
                app_date: formatToDDMMYYYY(entry.date),
                remarks: remarks || '',
                reason,
                mode: 'TEMP',
                submode: 'SLTBK',
            },
        ];

        try {
            setLoading(true);
            const res = await Appointments.releasedBlockedSlot(payload);
            console.log(res, 'release api');

            if (res.success) {
                // message.success('Slot released successfully');
                message.success(res.message || 'Slot released successfully');
                setShowReasonBox(false);
                setReason('');
                // 🔑 trigger refresh in TimeSelector
                setShouldFetch((prev) => !prev); // toggle
            } else {
                message.error(res.message || 'Failed to release slot');
            }
        } catch (err) {
            message.error('Failed to release slot');
        } finally {
            setLoading(false);
        }
    };
    const popContent = (
        <div className="slot-popover blocked-slot">
            {/* Info banner */}

            <div className="slot-text">This slot is temporarily blocked {user}</div>

            {/* Header */}
            <div className="slot-header">
                <div className="slot-title">{package_code || 'No package'}</div>
                <div className="slot-expiry">
                    Expires in <span>{timeRemaining}</span>
                </div>
            </div>

            {/* Reason */}
            <div className="slot-section">
                <div className="slot-label">Reason for Blocking</div>
                <div className="slot-text">{blockReason || 'No reason provided'}</div>
            </div>

            {/* Remarks */}
            <div className="slot-section">
                <div className="slot-label">Remarks</div>
                <div className="slot-text">{remarks || '-'}</div>
            </div>

            {/* Divider line */}
            <div className="slot-divider" />

            {/* Release with reason */}
            {showReasonBox && (
                <TextArea
                    placeholder="Provide The Reason For Release"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="slot-textarea"
                />
            )}
            {releasePermission && (
                <Button type="primary" block onClick={handleRelease} className="release-btn" loading={loading}>
                    Release
                </Button>
            )}
        </div>
    );

    return (
        <AppPopover content={popContent} {...popoverProps}>
            {children}
        </AppPopover>
    );
}

/**  Used Slot Popover */
export function UsedSlotPopover({ entry, children }) {
    const guestDetails = entry?.guest_details;
    const popoverProps = useResponsivePopover();

    if (entry.sub_mode !== SLOT_STATUS.BOOKED) return children;

    const popContent = (
        <div className="slot-popover ">
            <div className="slot-header">
                {/* <div className="slot-title">{guestDetails?.package || entry.packageName || 'Used Package'}</div> */}
                <div className="slot-popover unused-slot">{guestDetails?.package || entry.packageName || 'Used Package'}</div>
            </div>
            {/* <div className="slot-info">
                <UserOutlined className="slot-icon" />
                {guestDetails?.name || 'Guest Name'}
            </div>

            <div className="slot-info">
                <MailFilled className="slot-icon" />
                {guestDetails?.email || 'guest@email.com'}
            </div>

            <div className="slot-info">
                <PhoneFilled className="slot-icon" />
                {guestDetails?.mobile || '0000000000'}
            </div>

            <div className="slot-staff">
                <Avatar size={22}>
                    {guestDetails?.user
                        ? guestDetails.user
                              .split('@')[0]
                              .split(/[^a-zA-Z]/)
                              .filter(Boolean)
                              .map((word) => word.charAt(0).toUpperCase())
                              .slice(0, 2)
                              .join('')
                        : 'SN'}
                </Avatar>
                <div className="staff-name">{guestDetails?.user || 'Staff Name'}</div>
            </div> */}
        </div>
    );

    return (
        <AppPopover content={popContent} {...popoverProps}>
            {children}
        </AppPopover>
    );
}

/**  Held Slot Popover */
export function HeldSlotPopover({ entry, children }) {
    const popoverProps = useResponsivePopover();
    if (entry.sub_mode !== SLOT_STATUS.HELD && entry.sub_mode !== SLOT_STATUS.HELD_WEB) return children;

    const popContent = <div className="slot-popover held-slot">This slot is On-Hold for few minutes. Please try again later</div>;

    return (
        <AppPopover content={popContent} {...popoverProps}>
            {children}
        </AppPopover>
    );
}

/**  Unused Slot Popover */
export function UnusedSlotPopover({ entry, children }) {
    const popoverProps = useResponsivePopover();
    if (entry.sub_mode !== SLOT_STATUS.UN_USED) return children;

    const popContent = <div className="slot-popover unused-slot">Oops, this slot has expired</div>;

    return (
        <AppPopover content={popContent} {...popoverProps}>
            {children}
        </AppPopover>
    );
}
